import { FC, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, ChevronLeftOutline, ChevronRightOutline, HStack, StyleProps, Text } from '@bonusly/design-system';

type PaginatorProps = StyleProps & {
	currentPage: number;
	onSelectPage: (pageNumber: number) => void;
	totalPages: number;
};

const usePages = (currentPage: number, totalPages: number) => {
	return useMemo(() => {
		const spread = Array.from({ length: 4 }, (_, i) => [currentPage - (i + 1), currentPage + (i + 1)])
			.concat([currentPage])
			.flat()
			.sort((a, b) => a - b)
			.filter((page) => page > 0 && page <= totalPages);

		while (spread.length > 5) {
			const first = spread[0];
			const dx = currentPage - first;

			const last = spread[spread.length - 1];
			const dy = last - currentPage;

			if (dx > dy) {
				spread.shift();
			} else {
				spread.pop();
			}
		}

		if (!spread.includes(1)) {
			spread.unshift(1);
		}

		if (!spread.includes(totalPages)) {
			spread.push(totalPages);
		}

		return spread;
	}, [currentPage, totalPages]);
};

export const Paginator: FC<PaginatorProps> = ({ currentPage, onSelectPage, totalPages, ...rest }) => {
	const { t } = useTranslation('components');
	const pages = usePages(currentPage, totalPages);

	return (
		<Box role="navigation" aria-label={t('paginated_table.pagination.navigation_label')} data-testid="pagination" {...rest}>
			<HStack list-style="none" spacing={{ base: 3, md: 7 }} justifyContent="center">
				{currentPage > 1 && (
					<ChevronLeftOutline
						color="text.default"
						aria-label={t('paginated_table.pagination.previous_page_label')}
						onClick={() => onSelectPage(currentPage - 1)}
						role="button"
						size="sm"
					/>
				)}
				{totalPages > 1 &&
					pages.map((page, i) => {
						const nextPage = pages[i + 1];
						const isEllipsis = nextPage && nextPage - page > 1;

						return isEllipsis ? (
							<Fragment key={i}>
								<PaginationButton isCurrent={page === currentPage} onSelect={onSelectPage} page={page} />
								<Text key={i}>...</Text>
							</Fragment>
						) : (
							<PaginationButton key={i} isCurrent={page === currentPage} onSelect={onSelectPage} page={page} />
						);
					})}
				{currentPage < totalPages && (
					<ChevronRightOutline
						color="text.default"
						onClick={() => onSelectPage(currentPage + 1)}
						aria-label={t('paginated_table.pagination.next_page_label')}
						role="button"
						size="sm"
					/>
				)}
			</HStack>
		</Box>
	);
};

interface PaginationButtonProps {
	isCurrent: boolean;
	onSelect: (page: number) => void;
	page: number;
}

export const PaginationButton: FC<PaginationButtonProps> = ({ isCurrent, onSelect, page }) => {
	const { t } = useTranslation('components');

	return (
		<Box>
			<Button
				bg="transparent"
				color="text.default"
				borderRadius="full"
				onClick={() => onSelect(page)}
				_active={{
					bg: 'primary.bgAlpha',
					color: 'primary.fgStrong',
				}}
				_hover={{
					bg: 'secondary.bgAlphaHover',
					color: 'text.default',
					_active: {
						bg: 'primary.bgAlphaHover',
						color: 'primary.fgStrongest',
					},
				}}
				_focus={{ border: 'none' }}
				{...(isCurrent && {
					'data-active': true,
				})}
				aria-label={
					isCurrent
						? t('paginated_table.pagination.current_page', {
								page_number: page,
							})
						: t('paginated_table.pagination.go_to_page', {
								page_number: page,
							})
				}
			>
				<Text fontSize="lg" fontWeight="medium" lineHeight={4}>
					{page}
				</Text>
			</Button>
		</Box>
	);
};
